import { LocaleSource, SupportedLocale } from '@amzn/red-velvet-api';
import {
  DEFAULT_LOCALE,
  DEFAULT_LOCALE_BY_LANGUAGE,
  P0_SUPPORTED_LANGUAGES,
} from '../data/supportedLanguages';

const OFFERED_LOCALES: readonly SupportedLocale[] = P0_SUPPORTED_LANGUAGES.map(
  ({ locale }) => locale
);

/** Matched from the languages the browser asks for. */
export const LOCALE_SOURCE_BROWSER = 'browser_detected';
/** Nothing identified a language, so the site is in its default one. */
export const LOCALE_SOURCE_DEFAULT = 'default_fallback';
/** Localization is switched off for this user, so the site is in English whatever they prefer. */
export const LOCALE_SOURCE_FLAG_OFF = 'flag_off';

/**
 * What decided the language a session is in.
 *
 * The API reports the two it knows about, a saved choice and the user's Amazon profile. The rest are
 * resolutions the API never sees, so they are named here.
 */
export type SessionLocaleSource =
  | LocaleSource
  | typeof LOCALE_SOURCE_BROWSER
  | typeof LOCALE_SOURCE_DEFAULT
  | typeof LOCALE_SOURCE_FLAG_OFF;

export type SessionLocale = {
  locale: SupportedLocale;
  source: SessionLocaleSource;
};

/** English because localization is switched off for this user, not because they asked for it. */
export const LOCALIZATION_OFF_LOCALE: SessionLocale = {
  locale: DEFAULT_LOCALE,
  source: LOCALE_SOURCE_FLAG_OFF,
};

/** Accepts the spellings browsers and PAPI actually send, such as `en_US` or `PT-br`. */
function normalizeTag(tag: string): string {
  return tag.trim().replace(/_/g, '-').toLowerCase();
}

function languageSubtagOf(tag: string): string {
  return normalizeTag(tag).split('-')[0];
}

function defaultLocaleForLanguage(languageSubtag: string): SupportedLocale | undefined {
  return Object.prototype.hasOwnProperty.call(DEFAULT_LOCALE_BY_LANGUAGE, languageSubtag)
    ? DEFAULT_LOCALE_BY_LANGUAGE[languageSubtag]
    : undefined;
}

/** The language the picker can show for this tag, or nothing if it offers none. */
export function toOfferedLocale(rawLocale: string | undefined): SupportedLocale | undefined {
  if (!rawLocale) {
    return undefined;
  }

  const normalizedTag = normalizeTag(rawLocale);

  return OFFERED_LOCALES.find((offered) => normalizeTag(offered) === normalizedTag);
}

/**
 * The best language for someone's browser settings.
 *
 * Each language the browser asks for is fully considered before moving to the next, so a user who
 * prefers French gets French even when the exact variant they asked for is unavailable and their
 * second choice is German. If they named another French variant further down the list, they get that
 * one rather than the default.
 */
export function matchBrowserLocale(
  requestedLanguages: readonly string[]
): SupportedLocale | undefined {
  const requestedTags = requestedLanguages.map(normalizeTag).filter((tag) => tag.length > 0);

  for (const tag of requestedTags) {
    const exactMatch = toOfferedLocale(tag);
    if (exactMatch) {
      return exactMatch;
    }

    const languageDefault = defaultLocaleForLanguage(languageSubtagOf(tag));
    if (!languageDefault) {
      continue;
    }

    const explicitlyRequestedVariant = requestedTags
      .map(toOfferedLocale)
      .find((offered) => offered && languageSubtagOf(offered) === languageSubtagOf(tag));

    return explicitlyRequestedVariant ?? languageDefault;
  }

  return undefined;
}

/** The languages this browser asks for, most preferred first. */
export function browserLanguages(): readonly string[] {
  return navigator.languages?.length
    ? navigator.languages
    : navigator.language
      ? [navigator.language]
      : [];
}

/**
 * The language this browser most prefers, in the spelling the browser used.
 *
 * Deliberately not matched against the languages the site offers: reporting it as asked for is what
 * tells us which languages people want that VC has yet to be translated into. Nothing when the
 * browser names no language at all.
 */
export function primaryBrowserLanguage(): string | undefined {
  return browserLanguages()
    .map((tag) => tag.trim())
    .find((tag) => tag.length > 0);
}

/**
 * The language a session runs in, and what decided it.
 *
 * Reports which step of the chain won, because that cannot be worked out after the fact: a saved
 * language looks the same whether the user picked it themselves or it came from their Amazon profile,
 * and only the API can tell those apart. A saved language the picker no longer offers is ignored, so
 * it is treated as though none were saved.
 */
export function resolveSessionLocale(
  preferredLocale: string | undefined,
  preferredLocaleSource: LocaleSource | undefined,
  requestedLanguages: readonly string[]
): SessionLocale {
  const savedLocale = toOfferedLocale(preferredLocale);
  if (savedLocale) {
    // The API names a source whenever it names a language, so this default stands in for a broken
    // contract. Counting the session as a choice keeps it in the report rather than dropping it.
    return {
      locale: savedLocale,
      source: preferredLocaleSource || LocaleSource.EXPLICIT_PREFERENCE,
    };
  }

  const browserLocale = matchBrowserLocale(requestedLanguages);
  if (browserLocale) {
    return { locale: browserLocale, source: LOCALE_SOURCE_BROWSER };
  }

  return { locale: DEFAULT_LOCALE, source: LOCALE_SOURCE_DEFAULT };
}

/**
 * The language the picker shows as current: the user's saved choice, else their browser's, else
 * English. A saved language the picker no longer offers is ignored so the dropdown is never blank.
 */
export function resolvePickerLocale(
  preferredLocale: string | undefined,
  requestedLanguages: readonly string[]
): SupportedLocale {
  return resolveSessionLocale(preferredLocale, undefined, requestedLanguages).locale;
}
